/**
 * Apply the user's saved TTS voice + pitch preferences to a SpeechSynthesisUtterance.
 *
 * Voice: chosen by the ♀/♂ picker in Settings, stored as voiceURI in localStorage.
 * Pitch: female → 1.2 (higher), male → 0.85 (lower), unset → 1.0 (neutral).
 *        This guarantees an audible difference even when the device only has
 *        one TTS voice for the selected language.
 * Speed: from the Settings speed picker (tts_rate), default 0.95. Applied here
 *        so every read-out (day, week, reminders) honours the chosen speed.
 */
/** App language code → BCP-47 tag understood by speechSynthesis. */
export function langToSpeechLang(lang) {
  const map = {
    it: 'it-IT', en: 'en-US', 'en-US': 'en-US', 'en-GB': 'en-GB',
    es: 'es-ES', 'es-ES': 'es-ES', 'es-AR': 'es-AR', 'es-MX': 'es-MX', 'es-CO': 'es-CO',
    de: 'de-DE', fr: 'fr-FR', nl: 'nl-NL',
    pt: 'pt-PT', 'pt-PT': 'pt-PT', 'pt-BR': 'pt-BR',
    ru: 'ru-RU', pl: 'pl-PL', ro: 'ro-RO', zh: 'zh-CN', ja: 'ja-JP', hi: 'hi-IN', ko: 'ko-KR', ar: 'ar-SA',
  };
  return map[lang] || map[String(lang || '').split('-')[0]] || 'it-IT';
}

/**
 * Wait until the phone has actually listed its voices.
 *
 * getVoices() fills in asynchronously, and on the first call after a page load
 * it usually returns an EMPTY array. applyTTSPrefs then finds no match for the
 * saved voice, leaves utterance.voice unset, and the engine falls back to
 * whatever default it likes. Press again a second later, the list has arrived,
 * and the chosen voice is used — so the same button reads the same words two
 * different ways, which is exactly what Silvia heard.
 *
 * Nothing here changes the voice. It only makes sure the list is there before
 * anybody looks at it.
 */
export function voicesReady(timeout = 1500) {
  return new Promise(resolve => {
    const synth = typeof window !== 'undefined' && window.speechSynthesis;
    if (!synth) { resolve([]); return; }
    const now = synth.getVoices && synth.getVoices();
    if (now && now.length) { resolve(now); return; }
    let done = false;
    // Guarded: not every speechSynthesis-shaped object has the full pair. One
    // that could be subscribed to but not unsubscribed from threw here and took
    // the whole screen down — found by the smoke test, which is the sort of
    // thing it is for.
    const finish = () => {
      if (done) return;
      done = true;
      try { synth.removeEventListener?.('voiceschanged', finish); } catch (_) {}
      resolve((synth.getVoices && synth.getVoices()) || []);
    };
    try { synth.addEventListener?.('voiceschanged', finish); } catch (_) {}
    // A phone that never fires the event must not leave the button silent.
    setTimeout(finish, timeout);
  });
}

export function applyTTSPrefs(utterance) {
  const savedURI    = localStorage.getItem('tts_voice');
  const savedGender = localStorage.getItem('tts_gender');
  const savedRate   = parseFloat(localStorage.getItem('tts_rate'));

  // Apply voice if one was found and stored
  if (savedURI && window.speechSynthesis) {
    const voices = window.speechSynthesis.getVoices();
    const voice  = voices.find(v => v.voiceURI === savedURI);
    if (voice) utterance.voice = voice;
  }

  // Always apply pitch — guarantees male ≠ female even with one voice
  if (savedGender === 'female')     utterance.pitch = 1.2;
  else if (savedGender === 'male')  utterance.pitch = 0.85;
  else                              utterance.pitch = 1.0;

  // Speed — user-chosen, else the default 0.95
  utterance.rate = (savedRate && savedRate > 0) ? savedRate : 0.95;

  return utterance;
}
